'use client';

import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';

export type MobileNavItem = {
  key: string;
  label: string;
  href: string;
  isActive: boolean;
};

/**
 * Hamburger + slide-in drawer for the operator nav at `<sm` breakpoints.
 *
 * The primary `<nav>` is `hidden sm:block` in BrandedHeader so below 640px
 * there's no other way to reach Sites/Scans/Settings. The drawer also
 * carries the operator email + change-password + logout that the desktop
 * header tucks behind `md:` utilities.
 *
 * Focus trap: TAB / SHIFT+TAB cycle within the panel while open. ESC and
 * backdrop click both close and return focus to the trigger.
 */
export function MobileNav({
  items,
  userEmail,
}: {
  items: MobileNavItem[];
  userEmail: string;
}) {
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const close = () => {
    setOpen(false);
    triggerRef.current?.focus();
  };

  useEffect(() => {
    if (!open) return;
    const root = panelRef.current;
    if (!root) return;
    const focusables = root.querySelectorAll<HTMLElement>(
      'a, button, input, [tabindex]:not([tabindex="-1"])',
    );
    focusables[0]?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
        return;
      }
      if (e.key !== 'Tab' || focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(true)}
        aria-expanded={open}
        aria-controls="mobile-nav-panel"
        aria-label="Open navigation menu"
        className="rounded border border-sonoma-primary-fg/30 p-2 hover:border-sonoma-accent hover:text-sonoma-accent sm:hidden"
      >
        <svg
          aria-hidden
          viewBox="0 0 24 24"
          className="size-5"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
        >
          <path d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>

      {open ? (
        <div className="fixed inset-0 z-50 sm:hidden">
          <button
            type="button"
            tabIndex={-1}
            aria-label="Close navigation menu"
            onClick={close}
            className="absolute inset-0 cursor-default bg-black/50"
          />
          <div
            id="mobile-nav-panel"
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-label="Navigation menu"
            className="absolute right-0 top-0 flex h-full w-72 max-w-[85vw] flex-col gap-3 bg-sonoma-primary p-4 text-sonoma-primary-fg shadow-lg"
          >
            <div className="flex justify-end">
              <button
                type="button"
                onClick={close}
                aria-label="Close navigation menu"
                className="rounded p-2 hover:bg-sonoma-primary-hover hover:text-sonoma-accent"
              >
                <svg
                  aria-hidden
                  viewBox="0 0 24 24"
                  className="size-5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  strokeLinecap="round"
                >
                  <path d="M6 6l12 12M18 6L6 18" />
                </svg>
              </button>
            </div>
            <nav aria-label="Primary (mobile)">
              <ul className="flex flex-col gap-1 text-base">
                {items.map((item) => (
                  <li key={item.key}>
                    <Link
                      href={item.href}
                      onClick={close}
                      aria-current={item.isActive ? 'page' : undefined}
                      className={
                        item.isActive
                          ? 'block rounded bg-sonoma-accent/20 px-3 py-2 font-medium text-sonoma-accent'
                          : 'block rounded px-3 py-2 hover:bg-sonoma-primary-hover hover:text-sonoma-accent'
                      }
                    >
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
            <div className="mt-auto space-y-1 border-t border-sonoma-primary-fg/20 pt-3 text-sm">
              <div className="px-3 py-1 text-sonoma-primary-fg/80">
                {userEmail}
              </div>
              <Link
                href="/change-password"
                onClick={close}
                className="block rounded px-3 py-2 hover:bg-sonoma-primary-hover hover:text-sonoma-accent"
              >
                Change password
              </Link>
              <form action="/logout" method="post" className="px-3 pt-1">
                <button
                  type="submit"
                  className="w-full rounded bg-sonoma-accent px-3 py-2 font-medium text-sonoma-primary hover:bg-sonoma-accent-hover"
                >
                  Log out
                </button>
              </form>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
